/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDate } from 'js-joda';
import _ from 'lodash';

import { AccessFormData } from 'forms/fieldsets/AccessFieldset';
import { FormFields } from 'forms/offerings/generic/OfferingPricingForm';
import { LoyaltyProgramFields } from 'forms/offerings/generic/OfferingLoyaltyProgramForm';
import { OfferingInformationFormData } from 'forms/offerings/generic/OfferingInformationForm';
import { OfferingInformationFormDataNew } from 'forms/offerings/generic/OfferingInformationFormNew';
import { TableKindDescription, dashboard } from 'types';
import { core } from '@tock/coretypes';
import { getCutoffFieldValues } from 'forms/fieldsets/FormAvailabilityCutOffFieldset';
import { isTicketTypeEventVariety } from '@tock/shared/utils/offerings';
import {
  ticketTypeVarietiesUsingPriceConfigs,
  varietyAndPriceToTicketTypePriceConfig,
} from 'utils/ticketUtils';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const {
  CommunicationPolicy,
  EventTemplate,
  EventTemplateListRequest,
  ExtraChargeDetails,
  Menu,
  MultiEventTemplateRequest,
  ReorderRequest,
  TicketType,
  TicketTypeCopyRequest,
  TicketTypeEventDateChangeRequest,
  TicketTypeGetRequest,
  TicketTypePricingApplyRequest,
  TicketTypePromoCode,
  TicketTypeQuestionChangeRequest,
} = protos;

export function fetch(
  ticketTypeId: number,
  { clearCurrentValue } = { clearCurrentValue: false }
): ApiAction {
  return {
    TockApi: {
      getProto: `/ticket/type/${ticketTypeId}`,
      type: AppConstants.TICKETS_FETCH_SINGLE,
      context: { clearCurrentValue },
    },
  };
}

export function selectTicketFromList(ticketType: core.TicketType): BaseTockAction<core.TicketType> {
  return {
    payload: ticketType,
    type: AppConstants.TICKETS_SELECT_FROM_LIST,
  };
}

export function fetchAll(
  filters?: {
    variety?: core.TicketType.Variety[];
    isArchived?: boolean;
    future?: boolean;
  },
  isAdditive?: boolean
): ApiAction {
  return {
    TockApi: {
      postListProto: '/ticket/type/list',
      request: TicketTypeGetRequest.fromObject(filters ?? {}),
      type: AppConstants.TICKETS_FETCH_ALL,
      context: { isAdditive },
    },
  };
}

export function fetchTockToGo(): ApiAction {
  return {
    TockApi: {
      postListProto: '/ticket/type/list',
      request: TicketTypeGetRequest.fromObject({
        variety: ['ORDER_DELIVERY', 'ORDER_PICKUP'],
      }),
      type: AppConstants.NULL,
    },
  };
}

export function add(ticket: core.TicketType): ApiAction {
  ticket.disableLoyaltyProgramAccrual = true;
  ticket.disableLoyaltyProgramRedemption = true;
  const request = TicketType.fromObject(ticket);
  const experienceType = isTicketTypeEventVariety(ticket) ? 'Event' : 'Experience';
  return {
    TockApi: {
      postProto: '/ticket/type',
      request,
      toast: `${experienceType} created`,
      type: AppConstants.TICKETS_ADD,
    },
  };
}

export function update(ticket: core.ITicketType, showToast = true): ApiAction {
  const request = TicketType.fromObject(ticket);
  return {
    TockApi: {
      putProto: '/ticket/type',
      request,
      toast: showToast ? 'Experience updated' : undefined,
      type: AppConstants.TICKETS_UPDATE,
    },
  };
}

export function updateAll(tickets: core.ITicketType[]): ApiAction {
  const requests = _.map(tickets, (ticket) => ({
    method: 'PUT',
    path: '/ticket/type',
    payload: TicketType.fromObject(ticket),
    requestType: TicketType,
  }));

  return {
    TockApi: {
      batchProto: requests,
      type: AppConstants.TICKETS_UPDATE_ALL,
    },
  };
}

export function archive(ticket: core.TicketType): ApiAction {
  const request = TicketType.fromObject(ticket);
  request.isArchived = true;

  return {
    TockApi: {
      putProto: '/ticket/type',
      request,
      toast: 'Experience archived',
      type: AppConstants.TICKETS_UPDATE,
    },
  };
}

export function unarchive(ticket: core.TicketType): ApiAction {
  const request = TicketType.fromObject(ticket);
  request.isArchived = false;

  return {
    TockApi: {
      putProto: '/ticket/type',
      request,
      toast: 'Experience unarchived',
      type: AppConstants.TICKETS_UPDATE,
    },
  };
}

export function copy(ticketTypeId: number, request: core.ITicketTypeCopyRequest): ApiAction {
  return {
    TockApi: {
      postProto: `/ticket/type/${ticketTypeId}/copy`,
      request: TicketTypeCopyRequest.fromObject(request),
      toast: 'Experience copied',
      type: AppConstants.TICKETS_ADD,
    },
  };
}

// New actions with a more limited interface
export function updateAccess(ticket: core.TicketType, newAccessInfo: AccessFormData): ApiAction {
  const newTicket = {
    ...ticket,
    ...newAccessInfo,
  };
  return update(newTicket);
}

export function updateFees(ticket: core.TicketType, fees: core.ITicketTypeFee[]): ApiAction {
  const newTicket = {
    ...ticket,
    fee: fees,
  };
  return update(newTicket);
}

export function updateMobileTicket(ticket: core.TicketType, mobileTicketFields: any): ApiAction {
  const newTicket = {
    ...ticket,
    ...mobileTicketFields,
  };
  return update(newTicket);
}

export function updateLoyaltyProgram(
  ticket: core.TicketType,
  loyaltyProgram: LoyaltyProgramFields
): ApiAction {
  const newTicket = {
    ...ticket,
    disableLoyaltyProgram: !loyaltyProgram.showLoyaltyProgram,
    disableLoyaltyProgramRedemption: !loyaltyProgram.redeemLoyaltyPoints,
    disableLoyaltyProgramAccrual: !loyaltyProgram.accrueLoyaltyPoints,
  };
  return update(newTicket);
}

export function updateImages(ticket: core.TicketType, images: core.UploadedImage[]): ApiAction {
  const newTicket = {
    ...ticket,
    image: images,
  };
  return update(newTicket);
}

export function updateCommunication(ticket: core.TicketType, newCommunicationInfo: any): ApiAction {
  // Conservatively limit what's being pulled over to avoid accidental updates
  // to unrelated parts of the ticket_type
  const changedProps = _.pick(newCommunicationInfo, [
    'checkoutPageText',
    'confirmationText',
    'reminderEmailText',
    'postVisitEmailText',
    'reminderNumDaysBefore',
    'shouldAllowPostVisitFeedback',
    'shouldSendReminderEmails',
    'shouldSendSmsReminder',
    'smsReminderType',
    'smsFixedReminderTime',
    'smsVariableReminderMinutesBefore',
    'smsSeatedAlertEnabled',
    'smsSeatedAlertCopy',
    'smsSeatedAlertMinutes',
    'shouldSendPostVisitEmails',
    'useDefaultCheckoutPageText',
    'useDefaultConfirmationText',
    'useDefaultReminderEmailText',
    'useDefaultPostVisitEmailText',
  ]);
  const newTicket = {
    ...ticket,
    ticketTypeDetails: {
      ...ticket.ticketTypeDetails,
      ...changedProps,
    },
  };
  return update(newTicket);
}

export function updateSurveyOnlyShowInReminderEmail(
  ticket: core.TicketType,
  onlyShowInQuestionnaireEmail: boolean
): ApiAction {
  const newTicket = {
    ...ticket,
    ticketTypeDetails: {
      ...ticket.ticketTypeDetails,
      onlyShowInQuestionnaireEmail,
    },
  };
  return update(newTicket);
}

export function updateSurveyReminderDays(
  ticket: core.TicketType,
  questionnaireNumDaysBeforeBooking: any
): ApiAction {
  const newTicket = {
    ...ticket,
    ticketTypeDetails: {
      ...ticket.ticketTypeDetails,
      questionnaireNumDaysBeforeBooking,
    },
  };
  return update(newTicket);
}

export function updateSurveyExpirationNumDaysBefore(
  ticket: core.TicketType,
  surveyExpirationNumDaysBefore: number
): ApiAction {
  const newTicket: core.TicketType = {
    ...ticket,
    ticketTypeDetails: {
      ...ticket.ticketTypeDetails,
      surveyExpirationNumDaysBefore,
    },
  };
  return update(newTicket);
}

export function updateShouldSurveyExpire(
  ticket: core.TicketType,
  shouldSurveyExpire: boolean
): ApiAction {
  const newTicket = {
    ...ticket,
    ticketTypeDetails: {
      ...ticket.ticketTypeDetails,
      shouldSurveyExpire,
    },
  };
  return update(newTicket);
}

export function updateShouldSendQuestionnaireReminderEmail(
  ticket: core.TicketType,
  shouldSendQuestionnaireReminderEmail: boolean
): ApiAction {
  const newTicket = {
    ...ticket,
    ticketTypeDetails: {
      ...ticket.ticketTypeDetails,
      shouldSendQuestionnaireReminderEmail,
    },
  };
  return update(newTicket);
}

export function updateSurveyIntroText(
  ticket: core.TicketType,
  questionnaireIntroText: string
): ApiAction {
  const newTicket = {
    ...ticket,
    ticketTypeDetails: {
      ...ticket.ticketTypeDetails,
      questionnaireIntroText,
    },
  };
  return update(newTicket);
}

// temporary - this can be removed/consolidated with the below version as soon as the Formik
// version of the offering information form is fully launched
export function updateInfoNew(
  ticket: core.TicketType,
  newTicketInfo: OfferingInformationFormDataNew,
  selectedMetaTag: core.MetaTag[]
): ApiAction {
  // Conservatively limit what's being pulled over to avoid accidental updates
  // to unrelated parts of the ticket_type
  const changedProps: Partial<OfferingInformationFormData> = _.pick(newTicketInfo, [
    'description',
    'name',
    'serviceType',
    'shortCode',
    'releasedUntil',
    'addonsCompactView',
    'reserveShippingTime',
    'showMenuLink',
    'posSku',
    'requiredAge',
    'orderMinimumCents',
  ]);

  changedProps.releasedUntil = changedProps.releasedUntil || undefined;
  changedProps.requiredAge = newTicketInfo.isAgeVerificationRequired
    ? changedProps.requiredAge
    : undefined;

  const changedDetailsProps = {
    ...getCutoffFieldValues(newTicketInfo),
    overrideBookingCutoff: newTicketInfo.bookingCutoffType !== 'DEFAULT',
    displayColor: newTicketInfo.displayColor,
  };

  const builtLocation = buildLocation(newTicketInfo);

  const newTicket = {
    ...ticket,
    ...changedProps,
    customerMetaTag: selectedMetaTag,
    ticketTypeDetails: {
      ...ticket.ticketTypeDetails,
      ...changedDetailsProps,
      ...builtLocation,
    },
  };
  return update(newTicket as core.TicketType);
}

export function updateInfo(
  ticket: core.TicketType,
  newTicketInfo: OfferingInformationFormData,
  selectedMetaTag: core.MetaTag[]
): ApiAction {
  // Conservatively limit what's being pulled over to avoid accidental updates
  // to unrelated parts of the ticket_type
  const changedProps: Partial<OfferingInformationFormData> = _.pick(newTicketInfo, [
    'description',
    'name',
    'serviceType',
    'shortCode',
    'releasedUntil',
    'addonsCompactView',
    'reserveShippingTime',
    'showMenuLink',
    'posSku',
    'requiredAge',
    'orderMinimumCents',
  ]);

  changedProps.releasedUntil = changedProps.releasedUntil || undefined;
  changedProps.requiredAge = newTicketInfo.isAgeVerificationRequired
    ? changedProps.requiredAge
    : undefined;

  const { cutoffType, cutoffDaysBefore, cutoffMinutesBefore, cutoffMinutesAfter, cutoffTime } =
    newTicketInfo;

  const changedDetailsProps = {
    ...getCutoffFieldValues({
      cutoffType,
      cutoffDaysBefore,
      cutoffMinutesBefore,
      cutoffMinutesAfter,
      cutoffTime,
    }),
    overrideBookingCutoff: newTicketInfo.overrideBookingCutoff,
    displayColor: newTicketInfo.displayColor,
  };

  const builtLocation = buildLocation(newTicketInfo);

  const newTicket = {
    ...ticket,
    ...changedProps,
    customerMetaTag: selectedMetaTag,
    ticketTypeDetails: {
      ...ticket.ticketTypeDetails,
      ...changedDetailsProps,
      ...builtLocation,
    },
  };
  return update(newTicket as core.TicketType);
}

export function updatePricing(
  ticket: core.TicketType,
  newTicketInfo: Partial<FormFields>,
  businessTax: core.BusinessTax[] | null,
  showToast = true
): ApiAction {
  let newTicket = buildNewTicketForPricingChange(ticket, newTicketInfo);
  newTicket = {
    ...newTicket,
    businessTax: businessTax || [],
  };
  return update(newTicket, showToast);
}

export function applyTicketTypePricing(
  ticketType: core.TicketType,
  applyStarting: string,
  showToast = true
) {
  return {
    TockApi: {
      postProto: '/ticket/type/pricing/apply',
      request: TicketTypePricingApplyRequest.fromObject({
        ticketTypeId: ticketType.id,
        startingDate: applyStarting,
      }),
      toast: showToast ? `Success! pricing changes applied to available reservations.` : undefined,
      type: AppConstants.NULL,
    },
  };
}

export interface LocationData {
  location: {
    city: string;
    country: string;
    address: string;
    address2: string;
    zipCode: string;
    state: string;
    name: string;
    googlePlacesId: string;
    lat: number;
    lng: number;
  };
  sameAsBusinessLocation: boolean;
}

export interface SharedBaseEventFormData extends LocationData {
  richAddress: string;
  businessDay: LocalDate;
  description: string;
  endTime: string;
  name: string;
  defaultPrice: number;
  shortCode: string;
  posSku: string;
  serviceType: number;
  startTime: string;
  image: core.UploadedImage[];
  hasDetailPage: boolean;
}

/**
 * Form data takes an array of business tax ids
 */
export interface SharedEventFormData extends SharedBaseEventFormData {
  businessTax: number[];
}

export interface SeatedEventFormData extends SharedEventFormData {
  tableKindDescriptions: TableKindDescription[];
}
export interface ReorderSurveyData {
  draggedItemIndex: number;
  hoveredItemIndex: number;
  questions: core.TicketTypeQuestion[];
  ticketTypeId: number;
}

export interface ReorderTicketsData {
  draggedItemIndex: number;
  hoveredItemIndex: number;
  tickets: core.TicketType[];
}

export function updateEventDetails(ticket: core.TicketType, newEventInfo: any) {
  const changedProps = _.pick(newEventInfo, [
    'businessDay',
    'endTime',
    'maxPerPerson',
    'startTime',
    'totalAvailable',
  ]);
  const newTicket = {
    ...ticket,
    eventDetails: {
      ...ticket.eventDetails,
      ...changedProps,
    },
  };
  return update(newTicket);
}

export function updateDateOfEvent(ticketTypeId: number, newDate: string) {
  const request = TicketTypeEventDateChangeRequest.fromObject({
    ticketTypeId,
    newDate,
  });

  return {
    TockApi: {
      putProto: '/ticket/type/event/date',
      request,
      toast: 'Event date updated',
      type: AppConstants.TICKETS_UPDATE_EVENT_DATE,
    },
  };
}

export function reorder(reorderTicketsData: ReorderTicketsData): BaseTockAction {
  return {
    type: AppConstants.TICKETS_REORDER,
    payload: {
      ...reorderTicketsData,
    },
  };
}

export function saveOrdering(ticketTypes: core.TicketType[]): ApiAction {
  const ordering = _.map(_.sortBy(ticketTypes, 'ordering'), (ticket, index) => {
    return {
      id: ticket.id,
      order: index,
    };
  });
  const request = ReorderRequest.fromObject({ ticketType: { ordering } });
  return {
    TockApi: {
      putProto: '/reorder',
      request,
      type: AppConstants.TICKETS_SAVE_ORDERING,
    },
  };
}

export function reorderSurvey(reorderSurveyData: ReorderSurveyData): BaseTockAction {
  return {
    type: AppConstants.TICKETS_SURVEY_REORDER,
    payload: {
      ...reorderSurveyData,
    },
  };
}

/**
 * Updates the questions assigned to an offering to match the contents of the
 * questionList param.
 */
export function updateSurvey(
  experienceId: number,
  {
    questionList,
    questionnaireNumDaysBeforeBooking,
    questionnaireIntroText,
  }: {
    questionList: core.ITicketTypeQuestion[];
    questionnaireNumDaysBeforeBooking?: number | null;
    questionnaireIntroText?: string | null;
  },
  showToast = true,
  toast = 'Pre-visit questions updated'
): ApiAction {
  const request = TicketTypeQuestionChangeRequest.fromObject({
    question: _.map(questionList, (question, index) => ({
      ...question,
      ordering: index,
      options: _.map(question.options, (option, index) => ({
        ...option,
        name: option.text,
        ordering: index,
      })),
    })),
    daysBeforeToSendSurvey: questionnaireNumDaysBeforeBooking,
    questionnaireIntroText,
  });

  return {
    TockApi: {
      putListProto: `/ticket/type/${experienceId}/question`,
      request,
      toast: showToast ? toast : undefined,
      type: AppConstants.TICKETS_SURVEY_UPDATE,
    },
  };
}

export function getDefaultQuestions(): ApiAction {
  return {
    TockApi: {
      getListProto: '/ticket/type/question/default/list',
      type: AppConstants.TICKET_DEFAULT_QUESTION_FETCH_ALL,
    },
  };
}

export function fetchCommunicationPolicyForExperience(experienceId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/ticket/type/${experienceId}/communication/admin`,
      type: AppConstants.TICKETS_COMMUNICATION_POLICY_FETCH,
    },
  };
}

export function updateCommunicationPolicyForExperience(
  experienceId: number,
  policy: core.ICommunicationPolicy
): ApiAction {
  const request = CommunicationPolicy.fromObject(policy);
  return {
    TockApi: {
      putProto: `/ticket/type/${experienceId}/communication`,
      request,
      type: AppConstants.TICKETS_COMMUNICATION_POLICY_UPDATE,
      toast: 'Communication policy updated',
    },
  };
}

function buildNewTicketForPricingChange(
  ticket: core.TicketType,
  newTicketInfo: Partial<FormFields>
): core.ITicketType {
  const newTicket: core.ITicketType = {
    ...ticket,
    taxCategoryId: newTicketInfo.taxCategoryId
      ? parseInt(newTicketInfo.taxCategoryId, 10)
      : undefined,
  };

  if (!_.isNil(newTicketInfo.variety)) {
    newTicket.variety = newTicketInfo.variety;

    if (ticketTypeVarietiesUsingPriceConfigs.includes(newTicket.variety)) {
      newTicket.ticketTypePriceConfig = [
        varietyAndPriceToTicketTypePriceConfig(
          newTicketInfo.variety,
          newTicketInfo.defaultPrice ? parseInt(newTicketInfo.defaultPrice, 10) : 0
        ),
      ];
    }
  }
  if (!_.isNil(newTicketInfo.defaultPrice)) {
    newTicket.defaultPrice = parseInt(newTicketInfo.defaultPrice, 10);
  }
  if (!_.isUndefined(newTicketInfo.largePartyPriceCents)) {
    // Null is a valid value, so we want this to be set even if newTicketInfo.largePartyPriceCents is null.
    // However we don't want anything to be updated if it's undefined.
    newTicket.largePartyPriceCents = _.isNull(newTicketInfo.largePartyPriceCents)
      ? newTicketInfo.largePartyPriceCents
      : parseInt(newTicketInfo.largePartyPriceCents, 10);
  }
  if (!_.isNil(newTicketInfo.eventFeeRate)) {
    newTicket.eventFeeRate = newTicketInfo.eventFeeRate;
  }
  if (!_.isNil(newTicketInfo.customFeeRate)) {
    newTicket.customFeeRate = newTicketInfo.customFeeRate;
    newTicket.customFeeName = newTicketInfo.customFeeName;
  }
  if (!_.isNil(newTicketInfo.taxIncludedInPrice)) {
    newTicket.ticketTypeDetails = {
      ...ticket.ticketTypeDetails,
      taxIncludedInPrice: newTicketInfo.taxIncludedInPrice === 'true',
    };
  }

  if (!_.isNil(newTicket.extraChargeDetails) && !_.isNil(newTicketInfo.extraChargeType)) {
    const newExtraChargeDetails = {
      serviceChargeName: newTicketInfo.serviceChargeName,
      extraChargeType: newTicketInfo.extraChargeType,
      serviceChargeRate: newTicketInfo.serviceChargeRate,
      serviceChargeTaxed: newTicketInfo.serviceChargeTaxed,
      gratuityOptionOne: newTicketInfo.gratuityOptionOne,
      gratuityOptionTwo: newTicketInfo.gratuityOptionTwo,
      gratuityOptionThree: newTicketInfo.gratuityOptionThree,
    } as any;
    const extraChargeDetails = ExtraChargeDetails.fromObject(newExtraChargeDetails);
    newTicket.extraChargeDetails = extraChargeDetails;
  }

  return newTicket;
}

export function fetchPromotions(experienceId: number): ApiAction {
  return {
    TockApi: {
      getListProto: `/ticket/type/${experienceId}/promo/code`,
      type: AppConstants.TICKET_PROMOTION_FETCH_ALL,
    },
  };
}

export function insertPromotion(
  experienceId: number,
  promoCode: core.ITicketTypePromoCode
): ApiAction {
  const request = TicketTypePromoCode.fromObject(promoCode);
  return {
    TockApi: {
      postProto: `/ticket/type/${experienceId}/promo/code`,
      request,
      toast: 'Promotion created',
      type: AppConstants.TICKET_PROMOTION_INSERT,
    },
  };
}

export function updatePromotion(
  experienceId: number,
  promoCode: core.ITicketTypePromoCode,
  toast?: string
): ApiAction {
  const request = TicketTypePromoCode.fromObject(promoCode);
  return {
    TockApi: {
      putProto: `/ticket/type/${experienceId}/promo/code`,
      request,
      toast: toast ? toast : 'Promotion updated',
      type: AppConstants.TICKET_PROMOTION_UPDATE,
    },
  };
}

export function insertTemplate(experienceId: number, template: core.IEventTemplate): ApiAction {
  const request: core.MultiEventTemplateRequest = MultiEventTemplateRequest.fromObject({
    template: [template],
  });
  return {
    TockApi: {
      postProto: `/ticket/type/${experienceId}/event-template`,
      request,
      toast: undefined,
      type: AppConstants.TICKET_EVENT_TEMPLATE_INSERT,
      onError: 'We were unable to create a schedule for this event. Please try again.',
    },
  };
}

export function updateTemplate(
  experienceId: number,
  templateId: number,
  template: core.IEventTemplate
) {
  const request = EventTemplate.fromObject(template);
  return {
    TockApi: {
      putProto: `/ticket/type/${experienceId}/event-template/${templateId}`,
      request,
      toast: undefined,
      type: AppConstants.TICKET_EVENT_TEMPLATE_UPDATE,
      onError: { 2034: _.noop, defaultToOriginal: true },
    },
  };
}

export function getTemplatesForEvent(ticketTypeId: number, { init } = { init: false }) {
  return getEventTemplates(ticketTypeId, { resetInitialization: init });
}

export function getTemplatesForEvents(ticketTypeIds: number[]) {
  return getTemplatesForEventsInternal(ticketTypeIds, AppConstants.TICKETS_EVENT_TEMPLATES_GET);
}

export function getEventTemplates(ticketTypeId: number, context = { resetInitialization: false }) {
  return {
    TockApi: {
      getListProto: `/ticket/type/${ticketTypeId}/event-templates`,
      type: AppConstants.TICKET_EVENT_TEMPLATES_GET,
      context,
    },
  };
}

function getTemplatesForEventsInternal(
  ticketTypeIds: number[],
  type: string,
  context = { resetInitialization: false }
) {
  const request: core.EventTemplateListRequest = {
    id: ticketTypeIds,
  };
  return {
    TockApi: {
      postListProto: `/event-templates`,
      request: EventTemplateListRequest.fromObject(request),
      toast: undefined,
      type,
      context,
    },
  };
}

interface LocationInfo {
  isAtBusinessLocation: boolean;
  location?: Partial<core.Location>;
}

function buildLocation(locationFormData: LocationData): LocationInfo {
  if (locationFormData.sameAsBusinessLocation) {
    return {
      isAtBusinessLocation: true,
      location: undefined,
    };
  }
  return {
    isAtBusinessLocation: false,
    location: locationFormData.location,
  };
}

export function getTicketTypeMenu(menuId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/menu/${menuId}`,
      type: AppConstants.TICKET_MENU_GET,
    },
  };
}

export function updateTicketTypeMenu(ticketTypeId: number, menu: dashboard.Menu): ApiAction {
  const request = Menu.fromObject({
    ...menu,
    ticketTypeId: [...(menu.ticketTypeId ?? []), ticketTypeId],
  });
  return {
    TockApi: {
      putProto: `/menu`,
      request,
      type: AppConstants.TICKET_MENU_UPDATE,
    },
  };
}

export function clearTicketTypeMenu(ticketTypeId: number, menu: dashboard.Menu): ApiAction {
  const request = Menu.fromObject({
    ...menu,
    ticketTypeId: (menu.ticketTypeId ?? []).filter((id) => id !== ticketTypeId),
  });
  return {
    TockApi: {
      putProto: `/menu`,
      request,
      type: AppConstants.TICKET_MENU_REMOVE,
    },
  };
}

export function clearTicketTypeMenuStore(): BaseTockAction {
  return {
    type: AppConstants.CLEAR_TICKET_MENU,
  };
}
